import {FC, useState, useEffect} from 'react'
import {Modal} from 'react-bootstrap'

type TableType = {
  id: number
  capacity: number
  description: string
}

type Table = {
  id?: number
  tableNumber: string
  tableTypeId: number
  customCapacity?: number
  positionX: number | null
  positionY: number | null
  isActive: number
  notes: string | null
}

type Props = {
  show: boolean
  onClose: () => void
  onSave: (data: Omit<Table, 'id'> & {id?: number}) => void
  table?: Table
  tableTypes: TableType[]
  action: 'create' | 'edit'
}

export const TableCRUDModal: FC<Props> = ({show, onClose, onSave, table, tableTypes, action}) => {
  const [formData, setFormData] = useState<Omit<Table, 'id'> & {id?: number}>({
    tableNumber: '',
    tableTypeId: 0,
    customCapacity: undefined,
    positionX: null,
    positionY: null,
    isActive: 1,
    notes: '',
  })

  const [errors, setErrors] = useState<{[key: string]: string}>({})

  useEffect(() => {
    if (show) {
      if (action === 'edit' && table) {
        setFormData({
          id: table.id,
          tableNumber: table.tableNumber,
          tableTypeId: table.tableTypeId,
          customCapacity: table.customCapacity,
          positionX: table.positionX,
          positionY: table.positionY,
          isActive: table.isActive,
          notes: table.notes || '',
        })
      } else {
        setFormData({
          tableNumber: '',
          tableTypeId: tableTypes[0]?.id || 0,
          customCapacity: undefined,
          positionX: null,
          positionY: null,
          isActive: 1,
          notes: '',
        })
      }
      setErrors({})
    }
  }, [show, table, action, tableTypes])

  const validate = () => {
    const newErrors: {[key: string]: string} = {}

    if (!formData.tableNumber.trim()) {
      newErrors.tableNumber = 'O número da mesa é obrigatório'
    }

    if (!formData.tableTypeId || formData.tableTypeId === 0) {
      newErrors.tableTypeId = 'Selecione um tipo de mesa'
    }

    // Se selecionou "Personalizado" (-1), validar capacidade customizada
    if (formData.tableTypeId === -1) {
      if (!formData.customCapacity || formData.customCapacity <= 0) {
        newErrors.customCapacity = 'Informe a capacidade da mesa'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    onSave(formData)
  }

  return (
    <Modal show={show} onHide={onClose} size='lg' centered>
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{action === 'create' ? 'Nova Mesa' : 'Editar Mesa'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='row'>
            <div className='col-md-6 mb-5'>
              <label className='form-label required'>Número da Mesa</label>
              <input
                type='text'
                className={`form-control ${errors.tableNumber ? 'is-invalid' : ''}`}
                value={formData.tableNumber}
                onChange={(e) => setFormData({...formData, tableNumber: e.target.value})}
                placeholder='Ex: M01, Mesa 1, A1'
              />
              {errors.tableNumber && <div className='invalid-feedback'>{errors.tableNumber}</div>}
              <div className='form-text'>Identificação única da mesa no ambiente</div>
            </div>

            <div className='col-md-6 mb-5'>
              <label className='form-label required'>Tipo/Capacidade</label>
              <select
                className={`form-select ${errors.tableTypeId ? 'is-invalid' : ''}`}
                value={formData.tableTypeId}
                onChange={(e) => {
                  const newTypeId = Number(e.target.value)
                  setFormData({
                    ...formData,
                    tableTypeId: newTypeId,
                    customCapacity: newTypeId === -1 ? formData.customCapacity : undefined,
                  })
                }}
              >
                <option value='0'>Selecione...</option>
                {tableTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.capacity} pessoa(s) - {type.description}
                  </option>
                ))}
                <option value='-1'>✨ Personalizado</option>
              </select>
              {errors.tableTypeId && <div className='invalid-feedback'>{errors.tableTypeId}</div>}
              <div className='form-text'>
                {formData.tableTypeId === -1
                  ? 'Defina uma capacidade customizada abaixo'
                  : 'Capacidade máxima da mesa'}
              </div>
            </div>

            {formData.tableTypeId === -1 && (
              <div className='col-md-6 mb-5'>
                <label className='form-label required'>Capacidade Customizada</label>
                <input
                  type='number'
                  className={`form-control ${errors.customCapacity ? 'is-invalid' : ''}`}
                  value={formData.customCapacity ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      customCapacity: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                  placeholder='Ex: 12'
                  min='1'
                  max='100'
                />
                {errors.customCapacity && (
                  <div className='invalid-feedback'>{errors.customCapacity}</div>
                )}
                <div className='form-text'>Quantas pessoas essa mesa comporta?</div>
              </div>
            )}
          </div>

          <div className='alert alert-info d-flex align-items-center mb-5'>
            <i className='bi bi-info-circle fs-2 me-3'></i>
            <div>
              <strong>Posicionamento no Mapa:</strong>
              <br />
              Após salvar, você pode arrastar a mesa no mapa para posicioná-la visualmente no
              ambiente.
            </div>
          </div>

          <div className='mb-5'>
            <label className='form-label'>Observações</label>
            <textarea
              className='form-control'
              rows={3}
              value={formData.notes || ''}
              onChange={(e) => setFormData({...formData, notes: e.target.value || null})}
              placeholder='Ex: Próxima à janela, reservada para grupos especiais...'
            ></textarea>
            <div className='form-text'>Informações adicionais sobre a mesa</div>
          </div>

          <div className='form-check form-switch mb-5'>
            <input
              className='form-check-input'
              type='checkbox'
              id='isActive'
              checked={formData.isActive === 1}
              onChange={(e) => setFormData({...formData, isActive: e.target.checked ? 1 : 0})}
            />
            <label className='form-check-label' htmlFor='isActive'>
              Mesa ativa
            </label>
            <div className='form-text'>
              Mesas inativas não aparecem no sistema de reservas
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={onClose}>
            Cancelar
          </button>
          <button type='submit' className='btn btn-primary'>
            {action === 'create' ? 'Criar Mesa' : 'Salvar Alterações'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
